/**
 * CSV upload parser and validation for Work Order detail rows.
 * Validates parsed CSV rows against WO field rules.
 * Dropdown values are imported from statusOptions (single source of truth).
 */
import { priorities, testTypes } from '../constants/statusOptions';
import { CSV_MAX_ROWS, CSV_MAX_FILE_SIZE_BYTES } from '../constants/config';

/**
 * FEAT-2026-0017: the borehole column is labelled "Location ID" in the template
 * (it always matched the location's id; the bare "Location" header read as a
 * free-text place name). The row key stays `Location` — the alias below maps
 * the new label onto it, and files with the old header still import.
 */
export const WO_LOCATION_ID_LABEL = 'Location ID';

export const WO_CSV_TEMPLATE_HEADERS = [
  'Sample Reference', 'Specimen Ref', 'Specimen Depth', WO_LOCATION_ID_LABEL, 'Depth Top', 'Depth Base',
  'Type', 'Test Name', 'Priority', 'Start Date', 'Instructions',
] as const;

const REQUIRED_HEADERS = ['Sample Reference', 'Test Name'];

const HEADER_ALIASES: Record<string, string> = {
  'sample id': 'Sample Reference',
  'test type': 'Test Name',
  'location id': 'Location',
  'borehole': 'Location',
  'borehole id': 'Location',
};

export interface WoFieldIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface WoCsvRowValidation {
  row: Record<string, string>;
  rowNumber: number;
  valid: boolean;
  errors: WoFieldIssue[];
  /**
   * FEAT-2026-0019: a single CSV line may list more than one test for the same
   * sample (e.g. "UCS; Atterberg"). Each requested test expands into its own
   * NormalizedWoRow (identical sample fields, distinct TestName) so the import
   * creates one WorkOrderDetail per test. `null` when the row has hard errors.
   */
  normalizedRows: NormalizedWoRow[] | null;
}

export interface NormalizedWoRow {
  SampleReference: string;
  SpecimenRef: string;
  SpecimenDepth: string;
  Location: string;
  DepthTop: string;
  DepthBase: string;
  Type: string;
  TestName: string;
  Priority: string;
  StartDate: string;
  Instructions: string;
}

export interface WoFileValidationError {
  message: string;
}

/**
 * FEAT-2026-0019: split a "Test Name" cell into one-or-more test names so a
 * single CSV line can request multiple tests for the same sample. Accepts
 * semicolon, pipe, or newline separators; commas are also honoured because a
 * quoted cell ("UCS, Atterberg") reaches us with the comma intact after
 * RFC-4180 parsing. Trims each entry, drops blanks, and de-duplicates
 * case-insensitively while preserving first-seen order.
 */
export function splitTestNames(raw: string): string[] {
  const parts = (raw ?? '')
    .split(/[;,|\n]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const lower = p.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(p);
  }
  return out;
}

/** Case-insensitive match against a readonly array, returns canonical form or null */
function matchEnum(value: string, options: readonly string[]): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  return options.find((o) => o.toLowerCase() === lower) ?? null;
}

/** Validate file-level constraints before parsing rows */
export function validateWoFile(file: File): WoFileValidationError | null {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return { message: 'Please upload a .csv file.' };
  }
  if (file.size > CSV_MAX_FILE_SIZE_BYTES) {
    return { message: 'File exceeds 2 MB limit.' };
  }
  return null;
}

/** Validate CSV headers — returns error or null */
export function validateWoHeaders(headers: string[]): WoFileValidationError | null {
  const normalized = headers.map((h) => h.trim().toLowerCase());
  for (const req of REQUIRED_HEADERS) {
    const directMatch = normalized.includes(req.toLowerCase());
    const aliasMatch = Object.entries(HEADER_ALIASES).some(
      ([alias, target]) => target === req && normalized.includes(alias),
    );
    if (!directMatch && !aliasMatch) {
      return {
        message: `CSV headers do not match the template. Missing required column: "${req}". Expected: ${WO_CSV_TEMPLATE_HEADERS.join(', ')}`,
      };
    }
  }
  return null;
}

/** Remap legacy header aliases in parsed rows (mutates row keys) */
export function applyWoHeaderAliases(rows: Record<string, string>[]): void {
  for (const row of rows) {
    for (const [alias, target] of Object.entries(HEADER_ALIASES)) {
      for (const key of Object.keys(row)) {
        if (key.toLowerCase() === alias && !(target in row)) {
          row[target] = row[key];
        }
      }
    }
  }
}

/** Validate post-parse constraints (row count, empty file) */
export function validateWoRowCount(rowCount: number): WoFileValidationError | null {
  if (rowCount === 0) {
    return { message: 'CSV file contains no data rows. Please add data below the header row.' };
  }
  if (rowCount > CSV_MAX_ROWS) {
    return { message: `CSV exceeds maximum of ${CSV_MAX_ROWS} rows. Please split into smaller files.` };
  }
  return null;
}

/** Validate a single row and return normalized data or errors */
export function validateWoRow(
  row: Record<string, string>,
  rowNumber: number,
): WoCsvRowValidation {
  const errors: WoFieldIssue[] = [];

  // Required: Sample Reference
  const sampleRef = (row['Sample Reference'] ?? '').trim();
  if (!sampleRef) {
    errors.push({
      field: 'Sample Reference', severity: 'error',
      message: `Sample Reference is required (row ${rowNumber})`,
    });
  }

  // Optional: Specimen Ref, Specimen Depth, Location, Depth Top, Depth Base, Type
  const specimenRef = (row['Specimen Ref'] ?? '').trim();
  const specimenDepth = (row['Specimen Depth'] ?? '').trim();
  const location = (row['Location'] ?? '').trim();
  const depthTop = (row['Depth Top'] ?? '').trim();
  const depthBase = (row['Depth Base'] ?? '').trim();
  const sampleType = (row['Type'] ?? '').trim();

  // Required: Test Name — one CSV line may list multiple tests for the sample
  // (FEAT-2026-0019). Split, validate each against testTypes, and keep every
  // requested test so the import creates one WorkOrderDetail per test.
  const testNames = splitTestNames(row['Test Name'] ?? '');
  const resolvedTestNames: string[] = [];
  if (testNames.length === 0) {
    errors.push({
      field: 'Test Name', severity: 'error',
      message: `Test Name is required (row ${rowNumber})`,
    });
  } else {
    for (const raw of testNames) {
      const matched = matchEnum(raw, testTypes);
      if (!matched) {
        resolvedTestNames.push(raw);
        errors.push({
          field: 'Test Name', severity: 'warning',
          message: `Unknown Test Name '${raw}' — not in standard list: ${testTypes.join(', ')} (row ${rowNumber})`,
        });
      } else {
        resolvedTestNames.push(matched);
      }
    }
  }

  // Optional: Priority — validate against priorities, default Normal
  const priorityRaw = (row['Priority'] ?? '').trim();
  let priority = 'Normal';
  if (priorityRaw) {
    const matched = matchEnum(priorityRaw, priorities);
    if (!matched) {
      errors.push({
        field: 'Priority', severity: 'warning',
        message: `Unknown Priority '${priorityRaw}' — defaulting to 'Normal' (row ${rowNumber})`,
      });
    } else {
      priority = matched;
    }
  }

  // Optional: Start Date — validate format if provided
  const startDateRaw = (row['Start Date'] ?? '').trim();
  let startDate = '';
  if (startDateRaw) {
    const parsed = Date.parse(startDateRaw);
    if (isNaN(parsed)) {
      errors.push({
        field: 'Start Date', severity: 'warning',
        message: `Invalid date format '${startDateRaw}' — field left blank (row ${rowNumber})`,
      });
    } else {
      startDate = new Date(parsed).toISOString().split('T')[0];
    }
  }

  // Optional: Instructions (max 500 chars)
  let instructions = (row['Instructions'] ?? '').trim();
  if (instructions.length > 500) {
    errors.push({
      field: 'Instructions', severity: 'warning',
      message: `Instructions exceeds 500 characters — truncated (row ${rowNumber})`,
    });
    instructions = instructions.slice(0, 500);
  }

  const hasHardErrors = errors.some((e) => e.severity === 'error');
  const valid = !hasHardErrors;
  const normalizedRows: NormalizedWoRow[] | null = valid
    ? resolvedTestNames.map((TestName) => ({
      SampleReference: sampleRef,
      SpecimenRef: specimenRef,
      SpecimenDepth: specimenDepth,
      Location: location,
      DepthTop: depthTop,
      DepthBase: depthBase,
      Type: sampleType,
      TestName,
      Priority: priority,
      StartDate: startDate,
      Instructions: instructions,
    }))
    : null;

  return { row, rowNumber, valid, errors, normalizedRows };
}

/**
 * Check for duplicate Sample Reference + Test Name across the uploaded file.
 * A single line can now expand to several (sample, test) pairs
 * (FEAT-2026-0019), so duplicates are checked per pair: a duplicate test is
 * dropped from that line (warning) rather than failing the whole line. A line
 * is only marked invalid if every one of its tests turns out to be a duplicate.
 */
export function checkWoInFileDuplicates(validations: WoCsvRowValidation[]): void {
  const seen = new Map<string, number>();
  for (const v of validations) {
    if (!v.normalizedRows) continue;
    const kept: NormalizedWoRow[] = [];
    for (const n of v.normalizedRows) {
      const key = `${n.SampleReference}|${n.TestName}`;
      const prev = seen.get(key);
      if (prev !== undefined) {
        v.errors.push({
          field: 'Test Name', severity: 'warning',
          message: `Duplicate: '${n.SampleReference}' with test '${n.TestName}' already exists in this file — skipped (row ${v.rowNumber}, first seen row ${prev})`,
        });
      } else {
        seen.set(key, v.rowNumber);
        kept.push(n);
      }
    }
    if (kept.length === 0) {
      v.valid = false;
      v.normalizedRows = null;
      v.errors.push({
        field: 'Sample Reference', severity: 'error',
        message: `All tests on row ${v.rowNumber} duplicate earlier rows — nothing to import`,
      });
    } else {
      v.normalizedRows = kept;
    }
  }
}

/** Trigger a browser download of the WO CSV template */
export function downloadWoCsvTemplate(): void {
  const csv = WO_CSV_TEMPLATE_HEADERS.join(',') + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'work-order-upload-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}
